import { clsx } from "clsx";
import { DEFAULT_ELEMENT, type TypographyBaseProps } from "./types";
import { buildTypographyClasses } from "./utils";

export interface ClampTextProps extends TypographyBaseProps {
  /** Clamp text to N lines (1–6). */
  lines?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Single-line truncation with ellipsis. Overrides `lines`. */
  truncate?: boolean;
  /** Prevent wrapping (no ellipsis — pairs with explicit overflow). */
  nowrap?: boolean;
  /** Native tooltip — pass the full text so clamped content stays readable on hover. */
  title?: string;
}

export function ClampText({
  variant,
  tone,
  as,
  className,
  children,
  lines,
  truncate = false,
  nowrap = false,
  dir,
  lang,
  "data-testid": dataTestId,
  ...rest
}: ClampTextProps) {
  const Component = as ?? DEFAULT_ELEMENT[variant];
  const typographyClasses = buildTypographyClasses({ variant, tone, className });

  const clampClasses = clsx({
    truncate,
    "whitespace-nowrap": nowrap && !truncate,
    "line-clamp-1": !truncate && lines === 1,
    "line-clamp-2": !truncate && lines === 2,
    "line-clamp-3": !truncate && lines === 3,
    "line-clamp-4": !truncate && lines === 4,
    "line-clamp-5": !truncate && lines === 5,
    "line-clamp-6": !truncate && lines === 6,
  });

  return (
    <Component
      className={clsx(typographyClasses, clampClasses)}
      dir={dir}
      lang={lang}
      data-testid={dataTestId}
      {...rest}
    >
      {children}
    </Component>
  );
}
